import React, { useCallback, useEffect, useMemo } from 'react'
import { Col, Form, Row, Spin } from 'antd'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormCheckbox,
  FormInputNumber,
  FormSelect,
  FormTextareaInput,
} from '../../../common/components/sections'
import {
  StyledPageHeader,
  StyledPageTitle,
} from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import {
  createSelectOptions,
  showErrorNotification,
} from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { generateArrayOfNumbers } from '../../../common/utils'

import { useUpdateCourseTypeDetailsMutation } from '../../hooks'
import { courseTypePropType } from '../../prop-types'

import { courseTypeSimpleDetailsFormInitialValues } from './initial-values'

const CourseTypeSimpleDetailsForm = ({ courseType, maxPosition }) => {
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.COURSE_TYPE.UPDATE_DETAILS,
  ])

  const [
    updateCourseTypeDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateCourseTypeDetailsMutation()

  const initialValues = useMemo(
    () => courseTypeSimpleDetailsFormInitialValues(courseType),
    [courseType],
  )

  const positionOptions = useMemo(
    () => createSelectOptions(generateArrayOfNumbers(maxPosition)),
    [maxPosition],
  )

  const handleSaveData = useCallback(
    data => updateCourseTypeDetails(courseType.id, data),
    [courseType.id, updateCourseTypeDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Course Type details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [
    initialValues.synonyms,
    initialValues.isExcludedFromSearch,
    initialValues.position,
    initialValues.description,
    initialValues.isDefaultFilter,
    initialValues.isIgnored,
    initialValues.rearrangementScore,
    resetForm,
  ])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="editCourseType"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <StyledPageHeader>
        <StyledPageTitle>{courseType.name}</StyledPageTitle>
      </StyledPageHeader>

      <Row gutter={[16]}>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="description"
            label="Description"
            labelAbove
            placeholder="Input description..."
            autoSize
          />
          <FormCheckbox
            disabled={!isFormEnabled}
            name="isDefaultFilter"
            label="Default Filter"
          />
          <FormCheckbox
            disabled={!isFormEnabled}
            name="isIgnored"
            label="Ignored"
          />

          <FormCheckbox
            disabled={!isFormEnabled}
            label="Exclude from mobile app search"
            name="isExcludedFromSearch"
          />
        </Col>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="synonyms"
            label="Synonyms (separated by new line)"
            labelAbove
            placeholder="Input synonyms..."
            autoSize
          />
        </Col>
      </Row>
      <Row gutter={[16]}>
        <Col span={2}>
          <FormSelect
            disabled={!isFormEnabled}
            name="position"
            labelAbove
            label="Position"
            showSearch={false}
            allowClear={false}
            options={positionOptions}
            placeholder="Select position..."
          />
        </Col>
        <Col span={4}>
          <FormInputNumber
            disabled={!isFormEnabled}
            label="Rearrangement Score"
            name="rearrangementScore"
            labelAbove
            min={0}
            max={100}
          />
        </Col>
      </Row>
      <FormButtons
        permissions={[PERMISSIONS.COURSE_TYPE.UPDATE_DETAILS]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

CourseTypeSimpleDetailsForm.propTypes = {
  courseType: courseTypePropType.isRequired,
  maxPosition: PropTypes.number.isRequired,
}

export default CourseTypeSimpleDetailsForm
